import { useEffect } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { env } from "@/config/env";
import { StandaloneLayout } from "@/components/layout/StandaloneLayout";
import { MessageScreen } from "@/components/MessageScreen";
import { OfferPickerScreen } from "@/features/intake/OfferPickerScreen";
import { StartScreen } from "@/features/intake/StartScreen";
import { StatusScreen } from "@/features/status/StatusScreen";
import {
  CheckoutCancelRedirect,
  CheckoutSuccessRedirect,
} from "@/features/checkout/CheckoutReturnRedirect";
import { HorizonScanningLayout } from "@/features/horizonscanning/HorizonScanningLayout";
import { HorizonScanningWizard } from "@/features/horizonscanning/HorizonScanningWizard";
import { CheckEmailScreen } from "@/features/horizonscanning/CheckEmailScreen";
import { ConfirmScreen } from "@/features/horizonscanning/ConfirmScreen";
import { WatchlistScreen } from "@/features/horizonscanning/WatchlistScreen";
import { RecipientConfirmScreen } from "@/features/horizonscanning/RecipientConfirmScreen";
import { ColleaguesScreen } from "@/features/horizonscanning/ColleaguesScreen";
import { FeedbackScreen } from "@/features/horizonscanning/FeedbackScreen";
import { UnsubscribeScreen } from "@/features/horizonscanning/UnsubscribeScreen";

function HomeRoute() {
  const { search } = useLocation();
  if (env.defaultOfferId) {
    return <Navigate to={`/${env.defaultOfferId}/start${search}`} replace />;
  }
  return <OfferPickerScreen />;
}

/** Full page load so static HTML under public/ is served (not SPA catch-all). */
function HardRedirect({ to }: { to: string }) {
  useEffect(() => {
    const dest = new URL(to, window.location.origin);
    const incoming = new URLSearchParams(window.location.search);
    incoming.forEach((value, key) => {
      if (!dest.searchParams.has(key)) dest.searchParams.set(key, value);
    });
    if (!dest.hash) dest.hash = window.location.hash;
    window.location.replace(dest.pathname + dest.search + dest.hash);
  }, [to]);
  return null;
}

/** /monitoring/* → /horizonscanning/* (keeps query and hash). */
function LegacyMonitoringRedirect() {
  const { pathname, search, hash } = useLocation();
  const next = pathname.replace(/^\/monitoring(?=\/|$)/, "/horizonscanning");
  return <Navigate to={`${next}${search}${hash}`} replace />;
}

/** Standalone (full-page) app shell + routes. */
export function App() {
  return (
    <Routes>
      {/* Nested under /horizonscanning so these never match /:offerId/* */}
      <Route path="/horizonscanning">
        <Route index element={<HardRedirect to="/landing/horizonscanning/" />} />
        <Route element={<HorizonScanningLayout />}>
          <Route path="start" element={<HorizonScanningWizard />} />
          <Route path="check-email" element={<CheckEmailScreen />} />
          <Route path="confirm" element={<ConfirmScreen />} />
          <Route path="watchlist" element={<WatchlistScreen />} />
          <Route path="colleagues" element={<ColleaguesScreen />} />
          <Route path="recipient-confirm" element={<RecipientConfirmScreen />} />
          <Route path="feedback" element={<FeedbackScreen />} />
          <Route path="unsubscribe" element={<UnsubscribeScreen />} />
        </Route>
      </Route>
      <Route path="/monitoring" element={<LegacyMonitoringRedirect />} />
      <Route path="/monitoring/*" element={<LegacyMonitoringRedirect />} />
      <Route element={<StandaloneLayout />}>
        <Route path="/" element={<HomeRoute />} />
        <Route path="/:offerId/start" element={<StartScreen />} />
        <Route path="/:offerId/preview/:reportId" element={<StatusScreen />} />
        <Route path="/status/:reportId" element={<StatusScreen />} />
        <Route path="/success" element={<CheckoutSuccessRedirect />} />
        <Route path="/cancel" element={<CheckoutCancelRedirect />} />
        <Route
          path="*"
          element={
            <MessageScreen
              tone="neutral"
              title="Page not found"
              description="The page you're looking for doesn't exist."
            />
          }
        />
      </Route>
    </Routes>
  );
}
